import React, { useState } from "react";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";

interface U2000FormStateDebugPanelProps<T extends Record<string, any>> {
  formStateHandlers: U2000FormStateHandlers<T>;
}

function safeStringify(obj: unknown): string {
  try {
    if (obj === null) return "null";
    if (obj === undefined) return "undefined";
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
}

interface CollapsibleSectionProps {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  titleClassName?: string;
}

function CollapsibleSection({ title, open, onToggle, children, titleClassName = "text-gray-600" }: CollapsibleSectionProps) {
  return (
    <section className="border border-gray-200 rounded-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className={`flex items-center justify-between w-full px-2 py-1 text-left text-xs font-semibold ${titleClassName} bg-gray-50 hover:bg-gray-100 border-b border-gray-200`}
      >
        <span>{title}</span>
        <span className="text-gray-400">{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="p-1.5">{children}</div>}
    </section>
  );
}

function U2000FormStateDebugPanel<T extends Record<string, any>>({ formStateHandlers }: U2000FormStateDebugPanelProps<T>) {
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [errorsOpen, setErrorsOpen] = useState(true);
  const [touchedOpen, setTouchedOpen] = useState(false);
  const [dataOpen, setDataOpen] = useState(false);
  const [oldDataOpen, setOldDataOpen] = useState(false);
  const [fieldsInErrorOpen, setFieldsInErrorOpen] = useState(true);
  const [otherErrorsOpen, setOtherErrorsOpen] = useState(true);
  const [customAttrsOpen, setCustomAttrsOpen] = useState(false);

  const formState = formStateHandlers.getFormState();
  const editMode = formStateHandlers.getFormEditMode();
  const isSubmitted = formStateHandlers.isSubmitted();
  const isValid = formStateHandlers.isValid();
  const data = formStateHandlers.getData();
  const oldData = formStateHandlers.getOldData();
  const touched = formStateHandlers.getTouched();
  const errors = formStateHandlers.getErrors();
  const otherErrors = formStateHandlers.getOtherErrors();
  const customAttributes = formStateHandlers.getCustomAttributes();

  const hasErrors = errors && Object.keys(errors).length > 0;
  const errorFields = hasErrors ? Object.keys(errors) : [];
  const hasTouched = touched && Object.keys(touched).length > 0;
  const hasOtherErrors = otherErrors && Object.keys(otherErrors).length > 0;
  const hasCustomAttrs = customAttributes && Object.keys(customAttributes).length > 0;

  return (
    <div
      className="fixed bottom-4 right-4 z-999 w-[480px] max-h-[80vh] flex flex-col rounded-lg border border-gray-300 bg-white/95 shadow-lg backdrop-blur-xs"
      aria-label="Panneau debug état formulaire"
    >
      <button
        type="button"
        onClick={() => setPanelCollapsed((c) => !c)}
        className="flex items-center justify-between w-full px-3 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-t-lg border-b border-gray-200"
      >
        <span>Form state</span>
        <span className="text-gray-500">{panelCollapsed ? "▼" : "▲"}</span>
      </button>
      {!panelCollapsed && (
        <div className="overflow-y-auto p-2 text-xs font-mono space-y-2 flex-1 min-h-0">
          {/* État (toujours visible) */}
          <section>
            <div className="font-semibold text-gray-600 mb-1">État</div>
            <div className="space-y-0.5 text-gray-800">
              <div>
                state: <span className="text-blue-600">{formState}</span>
              </div>
              <div>
                editMode: <span className="text-blue-600">{editMode}</span>
              </div>
              <div>
                isSubmitted: <span className={isSubmitted ? "text-amber-600" : "text-gray-600"}>{String(isSubmitted)}</span>
              </div>
              <div>
                isValid: <span className={isValid ? "text-green-600" : "text-red-600"}>{String(isValid)}</span>
              </div>
            </div>
          </section>

          {/* Champs en erreur — toujours affiché */}
          <CollapsibleSection
            title={`Champs en erreur (${errorFields.length})`}
            open={fieldsInErrorOpen}
            onToggle={() => setFieldsInErrorOpen((o) => !o)}
            titleClassName={hasErrors ? "text-red-600" : "text-gray-600"}
          >
            {hasErrors ? (
              <ul className="list-disc list-inside space-y-0.5 text-red-700">
                {errorFields.map((field) => (
                  <li key={field}>
                    <span className="font-medium">{field}</span>
                    {errors[field] != null && <span className="text-red-600/80"> — {String(errors[field])}</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-gray-500 italic">Aucun champ en erreur</div>
            )}
          </CollapsibleSection>

          {/* Errors (détail JSON) — toujours affiché */}
          <CollapsibleSection title="Errors" open={errorsOpen} onToggle={() => setErrorsOpen((o) => !o)} titleClassName={hasErrors ? "text-red-600" : "text-gray-600"}>
            {hasErrors ? (
              <pre className="whitespace-pre-wrap break-all text-red-700 bg-red-50 p-1.5 rounded-sm border border-red-200 max-h-32 overflow-y-auto">{safeStringify(errors)}</pre>
            ) : (
              <pre className="whitespace-pre-wrap break-all text-gray-500 bg-gray-50 p-1.5 rounded-sm border border-gray-200">{safeStringify(errors ?? {})}</pre>
            )}
          </CollapsibleSection>

          {/* Other errors */}
          {hasOtherErrors && (
            <CollapsibleSection title="Other errors" open={otherErrorsOpen} onToggle={() => setOtherErrorsOpen((o) => !o)} titleClassName="text-orange-600">
              <pre className="whitespace-pre-wrap break-all text-orange-700 bg-orange-50 p-1.5 rounded-sm border border-orange-200 max-h-24 overflow-y-auto">{safeStringify(otherErrors)}</pre>
            </CollapsibleSection>
          )}

          {/* Touched */}
          {hasTouched && (
            <CollapsibleSection title="Touched" open={touchedOpen} onToggle={() => setTouchedOpen((o) => !o)}>
              <pre className="whitespace-pre-wrap break-all text-gray-700 bg-gray-50 p-1.5 rounded-sm border border-gray-200 max-h-20 overflow-y-auto">{safeStringify(touched)}</pre>
            </CollapsibleSection>
          )}

          {/* Data (current) */}
          <CollapsibleSection title="Data (current)" open={dataOpen} onToggle={() => setDataOpen((o) => !o)}>
            <pre className="whitespace-pre-wrap break-all text-gray-800 bg-gray-50 p-1.5 rounded-sm border border-gray-200 max-h-40 overflow-y-auto">{safeStringify(data)}</pre>
          </CollapsibleSection>

          {/* Old data */}
          {oldData != null && (
            <CollapsibleSection title="Old data" open={oldDataOpen} onToggle={() => setOldDataOpen((o) => !o)}>
              <pre className="whitespace-pre-wrap break-all text-gray-700 bg-gray-50 p-1.5 rounded-sm border border-gray-200 max-h-32 overflow-y-auto">{safeStringify(oldData)}</pre>
            </CollapsibleSection>
          )}

          {/* Custom attributes */}
          {hasCustomAttrs && (
            <CollapsibleSection title="Custom attributes" open={customAttrsOpen} onToggle={() => setCustomAttrsOpen((o) => !o)}>
              <pre className="whitespace-pre-wrap break-all text-gray-700 bg-gray-50 p-1.5 rounded-sm border border-gray-200 max-h-24 overflow-y-auto">{safeStringify(customAttributes)}</pre>
            </CollapsibleSection>
          )}
        </div>
      )}
    </div>
  );
}

export default U2000FormStateDebugPanel;
